import React, { useState } from 'react';
import {
  applyTypography,
  BaseButton,
  policyUrl,
  useLang,
  useT,
} from '@repo/common';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/24px/icon_24px_check.svg';
import RefundDetailsButton from './RefundDetailsButton';
import {
  formatWorkshopPrice,
  toNumericPrice,
} from '../utils/priceUtils';

const PaymentSummary = ({
  total = '10,000won',
  originalPrice,
  // 금액 통화 — KRW(계좌이체 워크숍)는 "55,000 won", USD 는 상세 페이지와 같은 "15.00$"
  currency = 'USD',
  itemCount = 0,
  onPayNow,
  loading = false,
  disabled = false,
  onRefundDetails,
  showRefundDetailsLink = false,
  // 동의 박스 표시 여부 — 결제(Payments) 단계에서만 true. Booking/CheckDetails는 false(기존 동작).
  requireAgreement = false,
  agreed = false,
  onAgreeChange = () => {},
  // 버튼 라벨 오버라이드 — 계좌이체 워크숍은 "Pay now" 대신 "Continue"/"Reserve with bank transfer"
  payLabel,
  // 버튼 위에 띄우는 짧은 안내 (계좌이체: 입금 후 확정 안내)
  note = '',
}) => {
  const t = useT();
  const { lang } = useLang();
  // 체크 없이 Pay now 클릭 시 표시되는 에러 상태
  const [showAgreeError, setShowAgreeError] = useState(false);

  // 숫자·"10,000won"·"15.00$" 어떤 형태로 와도 숫자로 통일한 뒤 통화에 맞게 다시 표기한다.
  // 이전엔 통화를 모른 채 전부 "won" 을 붙여 15달러 워크숍이 "15 won" 으로 보였다.
  const isFreeLabel = (value) =>
    typeof value === 'string' && value.trim().toLowerCase() === 'free';
  const numericTotal = isFreeLabel(total) ? 0 : (toNumericPrice(total) ?? 0);
  const numericOriginal = toNumericPrice(originalPrice) ?? 0;

  const isFree = numericTotal <= 0;

  // "Free" 특가 배지에 보여줄 원래 가격
  const discountAmount =
    isFree && numericOriginal > 0
      ? formatWorkshopPrice(numericOriginal, currency)
      : null;

  const formattedTotal = isFree
    ? 'Free'
    : formatWorkshopPrice(numericTotal, currency);
  // 할인 전 가격은 실제로 더 클 때만 취소선으로 보여준다
  const formattedOriginalPrice =
    numericOriginal > numericTotal
      ? formatWorkshopPrice(numericOriginal, currency)
      : null;

  // 결제 처리 중 상태는 BaseButton의 isLoading으로 제어한다.
  const isButtonDisabled = itemCount === 0 || disabled;

  const showError = showAgreeError && !agreed;

  const handleToggleAgree = () => {
    const next = !agreed;
    onAgreeChange(next);
    if (next) setShowAgreeError(false);
  };

  const handlePayClick = () => {
    if (isButtonDisabled) return;
    // 동의가 필요한 단계에서 미체크 시: 결제로 진행하지 않고 에러 표시
    if (requireAgreement && !agreed) {
      setShowAgreeError(true);
      return;
    }
    onPayNow?.();
  };

  return (
    <div className='flex flex-col gap-[8px] tabletH:gap-[24px] items-center pb-0 px-0 pt-0 tabletH:pl-[24px] tabletH:pr-0 tabletH:pt-[70px] relative shrink-0 w-full tabletH:w-[444px]'>
      {/* Payment Summary Card */}
      <div className='bg-grayscale-gray00 border border-grayscale-gray02 flex flex-col items-start overflow-clip p-[16px] tabletH:p-[24px] relative rounded-[15px] shrink-0 w-full'>
        <div className='content-stretch flex flex-col gap-[6px] items-start justify-center not-italic pb-[24px] relative shrink-0 text-text-primary w-full whitespace-pre-wrap'>
          <p
            className={`${applyTypography('title3')} shrink-0 w-full`}
            style={{ lineHeight: '1.2' }}
          >
            {t('Payment Summary', '결제 요약')}
          </p>
          {itemCount > 0 && (
            <p
              className={`${applyTypography(
                'b16Strong',
              )} hidden tabletH:block shrink-0 w-full`}
              style={{ lineHeight: '1.5' }}
            >
              {t(`(${itemCount} items)`, `(${itemCount}개 항목)`)}
            </p>
          )}
        </div>

        <div className='border-t border-grayscale-gray02 flex flex-col items-end justify-center pb-[16px] tabletH:pb-[32px] pt-[16px] px-0 relative shrink-0 w-full'>
          <div className='flex flex-col gap-[8px] items-end justify-center relative shrink-0 w-full'>
            <div className='relative flex items-center justify-between w-full shrink-0'>
              <div className='relative flex flex-col items-start justify-center shrink-0'>
                <p className='font-["Mona_Sans:Medium",sans-serif] text-[14px] tabletH:text-[16px] leading-[1.4] tabletH:leading-[1.5] text-text-primary shrink-0'>
                  {t('Total :', '합계 :')}
                </p>
                <p className='font-["Mona_Sans:Regular",sans-serif] text-[12px] tabletH:text-[13px] leading-[1.4] text-text-placeholder shrink-0'>
                  {t('(Incl. VAT)', '(VAT 포함)')}
                </p>
              </div>
              <div className='content-stretch flex flex-col font-["Mona_Sans:Medium",sans-serif] items-end justify-center relative shrink-0 text-right'>
                {formattedOriginalPrice && (
                  <p
                    className={`${applyTypography(
                      'b14',
                    )} text-text-placeholder shrink-0 line-through`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {t(
                      formattedOriginalPrice,
                      formattedOriginalPrice.replace('won', '원'),
                    )}
                  </p>
                )}
                <p
                  className={`font-["Mona_Sans:Medium",sans-serif] text-[18px] tabletH:text-[20px] leading-[1.5] ${
                    itemCount === 0 ? 'text-text-primary' : 'text-text-accent'
                  } shrink-0`}
                >
                  {itemCount === 0
                    ? t('0 won', '0원')
                    : formattedTotal === 'Free'
                      ? t('Free', '무료')
                      : t(
                          `${formattedTotal?.replace('won', ' won') || '0 won'}`,
                          `${formattedTotal?.replace('won', '원') || '0원'}`,
                        )}
                </p>
              </div>
            </div>
            {isFree && originalPrice && (
              <div className='bg-purple-purple01 border border-purple-purple03 flex gap-[8px] items-center justify-center overflow-clip p-[8px] tabletH:p-[16px] relative rounded-[8px] shrink-0 w-full'>
                <div className='flex items-center justify-center shrink-0 size-[16px] tabletH:size-[20px] text-text-accent'>
                  <CheckIcon className='w-full h-full' />
                </div>
                <p className='font-["Mona_Sans:Medium",sans-serif] text-[12px] tabletH:text-[14px] leading-[1.4] text-text-accent shrink-0 text-center'>
                  {discountAmount
                    ? t(
                        `Save ${discountAmount} with this special offer`,
                        `특별 혜택으로 ${discountAmount}을 절약했어요`,
                      )
                    : t(
                        'This is a special free offer',
                        '특별 무료 혜택입니다',
                      )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 환불 정책 동의 (DL-010) — 결제 단계에서만 노출 */}
        {requireAgreement && (
          <div className='flex flex-col gap-[8px] items-start pb-[24px] relative shrink-0 w-full'>
            <div
              className={`flex gap-[8px] items-start justify-center p-[12px] relative rounded-[8px] shrink-0 w-full border ${
                showError
                  ? 'border-[#ff7474] bg-[rgba(255,239,239,0.5)]'
                  : 'border-grayscale-gray02'
              }`}
            >
              <BaseButton
                type='text'
                size='s'
                htmlType='button'
                isStatic
                onClick={handleToggleAgree}
                role='checkbox'
                aria-checked={agreed}
                aria-label={t(
                  'Agree to Refund & Cancellation Policy',
                  '환불 및 취소 정책에 동의',
                )}
                icon={
                  agreed ? (
                    <svg
                      width='12'
                      height='12'
                      viewBox='0 0 16 16'
                      fill='none'
                      aria-hidden='true'
                    >
                      <path
                        d='M3.5 8.5l3 3 6-7'
                        stroke='#ffffff'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : null
                }
                className={`mt-[2px] min-w-0 cursor-pointer p-0 rounded-[6px] size-[20px] shrink-0 border-[1.6px] transition-colors ${
                  agreed
                    ? 'bg-green-deep02 border-green-deep02'
                    : 'bg-grayscale-gray00 border-grayscale-gray02'
                }`}
              />
              <div className='flex flex-[1_0_0] flex-col gap-[4px] items-start min-w-px'>
                <p className='text-[14px] leading-[1.4] text-text-tertiary font-["Mona_Sans:Medium",sans-serif]'>
                  {t('I have read and agree to the', '다음 내용을 읽고 동의합니다:')}{' '}
                  <BaseButton
                    type='text'
                    size='s'
                    htmlType='button'
                    hideIcon
                    typo={false}
                    isStatic
                    onClick={onRefundDetails}
                    textWrapperClass='h-auto'
                    className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 align-baseline underline text-text-tertiary'
                  >
                    {t('Refund & Cancellation Policy', '환불 및 취소 정책')}
                  </BaseButton>
                  .
                </p>
                <p className='text-[12px] leading-[1.4] text-text-secondary font-["Mona_Sans:Regular",sans-serif]'>
                  {t(
                    "I understand that refunds may be limited after use begins, and that exceptions apply in cases such as duplicate payment, technical issues, or Koready's fault.",
                    '이용 시작 후에는 환불이 제한될 수 있으며, 중복 결제, 기술적 문제, Koready 측 사유 등의 경우 예외가 적용됨을 이해합니다.',
                  )}
                </p>
              </div>
            </div>

            {showError && (
              <div className='flex gap-[4px] items-center shrink-0'>
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 20 20'
                  fill='none'
                  aria-hidden='true'
                  className='shrink-0'
                >
                  <circle cx='10' cy='10' r='8' fill='#ff4550' />
                  <rect
                    x='9'
                    y='8.5'
                    width='2'
                    height='5.5'
                    rx='1'
                    fill='#fff'
                  />
                  <circle cx='10' cy='6' r='1.1' fill='#fff' />
                </svg>
                <p className='text-[14px] leading-[1.4] text-[#ff4550] font-["Mona_Sans:Medium",sans-serif]'>
                  {t(
                    'Please agree to the required terms to continue.',
                    '계속하려면 필수 약관에 동의해 주세요.',
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Pay now + 약관 안내 */}
        <div className='flex flex-col gap-[8px] items-start w-full shrink-0'>
          {note && (
            <div className='bg-grayscale-gray01 border border-grayscale-gray02 rounded-[8px] p-[12px] w-full'>
              <p className='text-[13px] leading-[1.5] text-text-secondary font-["Mona_Sans:Regular",sans-serif]'>
                {note}
              </p>
            </div>
          )}
          <BaseButton
            onClick={handlePayClick}
            type='primary'
            size='l'
            hideIcon
            isDisabled={isButtonDisabled}
            isLoading={loading}
            width='100%'
          >
            {payLabel || t('Pay now', '결제하기')}
          </BaseButton>
          {requireAgreement && (
            <p className='text-[12px] leading-[1.4] text-text-placeholder text-center w-full font-["Mona_Sans:Regular",sans-serif]'>
              {payLabel
                ? t(
                    `By clicking ${payLabel}, you agree to the`,
                    `${payLabel}를 누르면 다음에 동의하게 됩니다:`,
                  )
                : t(
                    'By clicking Pay now, you agree to the',
                    '결제하기를 누르면 다음에 동의하게 됩니다:',
                  )}{' '}
              <a
                href={policyUrl('terms', lang)}
                target='_blank'
                rel='noopener noreferrer'
                className='underline text-text-tertiary'
              >
                {t('Terms of Use', '이용약관')}
              </a>
              ,{' '}
              <a
                href={policyUrl('privacy', lang)}
                target='_blank'
                rel='noopener noreferrer'
                className='underline text-text-tertiary'
              >
                {t('Privacy Policy', '개인정보 처리방침')}
              </a>
              {t(', and Payment Terms.', ', 결제 약관.')}
            </p>
          )}
        </div>
      </div>

      {showRefundDetailsLink && (
        <RefundDetailsButton onClick={onRefundDetails} />
      )}
    </div>
  );
};

export default PaymentSummary;
